<template>
  <div class="user-menu">
    <q-btn
      flat
      dense
      round
      :aria-label="t('layout.userMenu')"
      class="user-menu-btn"
    >
      <q-avatar size="36px" :color="avatarUrl ? undefined : 'primary'" :text-color="avatarUrl ? undefined : 'white'">
        <img v-if="avatarUrl" :src="avatarUrl" :alt="t('layout.userMenu')" />
        <span v-else>{{ userInitials }}</span>
      </q-avatar>

      <q-tooltip>{{ t('layout.userMenu') }}</q-tooltip>

      <q-menu
        :offset="[0, 8]"
        class="user-menu-dropdown"
      >
        <q-list style="min-width: 200px">
          <q-item-label header>
            <div class="text-weight-medium">{{ authStore.user?.name || t('layout.user') }}</div>
            <div v-if="authStore.user?.email" class="text-caption text-grey">{{ authStore.user.email }}</div>
            <div v-if="userRoleTitle" class="text-caption text-grey">{{ userRoleTitle }}</div>
          </q-item-label>

          <q-separator />

          <q-item
            v-if="isVolcoraCashDrawerSupported"
            clickable
            v-close-popup
            :disable="openingCashDrawer"
            class="user-menu-item"
            @click="handleOpenCashDrawer"
          >
            <q-item-section avatar>
              <q-spinner
                v-if="openingCashDrawer"
                color="primary"
                size="1.25rem"
              />
              <q-icon v-else name="payments" />
            </q-item-section>
            <q-item-section>
              <q-item-label>{{ t('settings.receiptPrinterOpenDrawer') }}</q-item-label>
            </q-item-section>
          </q-item>

          <q-item
            clickable
            v-close-popup
            @click="navigateToProfile"
            class="user-menu-item"
          >
            <q-item-section avatar>
              <q-icon name="person" />
            </q-item-section>
            <q-item-section>
              <q-item-label>{{ t('settings.profile') }}</q-item-label>
            </q-item-section>
          </q-item>

          <q-item
            clickable
            v-close-popup
            :href="supportUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="user-menu-item"
          >
            <q-item-section avatar>
              <q-icon name="help" />
            </q-item-section>
            <q-item-section>
              <q-item-label>{{ t('layout.support') }}</q-item-label>
            </q-item-section>
          </q-item>

          <q-separator v-if="isElectronDesktop && electronAutoUpdate" />

          <q-item
            v-if="isElectronDesktop && electronAutoUpdate"
            clickable
            v-close-popup
            :disable="electronAutoUpdate.isManualCheckInProgress"
            class="user-menu-item"
            @click="handleCheckForUpdates"
          >
            <q-item-section avatar>
              <q-spinner
                v-if="electronAutoUpdate.isManualCheckInProgress"
                color="primary"
                size="1.25rem"
              />
              <q-icon v-else name="system_update_alt" />
            </q-item-section>
            <q-item-section>
              <q-item-label>{{ t('settings.checkForUpdates') }}</q-item-label>
            </q-item-section>
          </q-item>

          <q-separator />

          <q-item
            clickable
            v-close-popup
            @click="handleLogout"
            class="user-menu-item"
          >
            <q-item-section avatar>
              <q-icon name="logout" />
            </q-item-section>
            <q-item-section>
              <q-item-label>{{ t('auth.logout') }}</q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </q-menu>
    </q-btn>
  </div>
</template>

<script setup>
import { computed, inject, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useQuasar } from 'quasar'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'
import { electronAutoUpdateKey } from '@/composables/useAutoUpdate'
import {
  isVolcoraCashDrawerSupported as checkVolcoraCashDrawerSupported,
  openVolcoraCashDrawer
} from '@/composables/useVolcoraReceiptPrinter'

const { t } = useI18n()
const $q = useQuasar()
const router = useRouter()
const authStore = useAuthStore()
const userStore = useUserStore()
const openingCashDrawer = ref(false)

const electronAutoUpdate = inject(electronAutoUpdateKey, null)

const isElectronDesktop = computed(
  () => typeof window !== 'undefined' && !!window?.electronAPI
)

const isVolcoraCashDrawerSupported = computed(() => checkVolcoraCashDrawerSupported())

const supportUrl = 'https://help.bluapps.net'

const avatarUrl = computed(() => {
  return (
    authStore.user?.profile_picture_url ||
    authStore.user?.avatar_url ||
    userStore.profile?.profile_picture_url ||
    userStore.profile?.profile_picture_original_url ||
    userStore.profile?.profile_picture ||
    null
  )
})

const userRoleTitle = computed(() => {
  const roles = authStore.user?.roles
  if (!roles || !Array.isArray(roles) || roles.length === 0) return null
  const firstRole = roles[0]
  const key = `layout.roles.${firstRole}`
  const translated = t(key)
  return translated !== key ? translated : formatRoleName(firstRole)
})

function formatRoleName(roleName) {
  if (!roleName || typeof roleName !== 'string') return ''
  return roleName.replace(/[._-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

const userInitials = computed(() => {
  const name = authStore.user?.name
  if (!name || typeof name !== 'string') return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) {
    const first = parts[0].charAt(0)
    const last = parts[parts.length - 1].charAt(0)
    return (first + last).toUpperCase()
  }
  const first = parts[0]
  return first.length >= 2 ? first.substring(0, 2).toUpperCase() : first.charAt(0).toUpperCase()
})

function notifyVolcoraError (r, fallbackI18nKey) {
  const message = r?.error || t(fallbackI18nKey)
  const detail =
    typeof r?.errorDetail === 'string' && r.errorDetail.trim() ? r.errorDetail.trim() : ''
  $q.notify({
    type: 'negative',
    message,
    actions: [{ label: 'Dismiss', color: 'white' }],
    ...(detail
      ? { caption: detail, multiLine: true, timeout: 5000 }
      : { timeout: 5000 })
  })
}

const handleOpenCashDrawer = async () => {
  if (openingCashDrawer.value) return
  openingCashDrawer.value = true
  try {
    const r = await openVolcoraCashDrawer()
    if (r?.success) {
      $q.notify({ type: 'positive', message: t('settings.receiptPrinterDrawerOk') })
    } else if (r?.error === 'drawer_not_configured') {
      $q.notify({ type: 'warning', message: t('settings.receiptPrinterDrawerFailed') })
    } else {
      notifyVolcoraError(r, 'settings.receiptPrinterDrawerFailed')
    }
  } finally {
    openingCashDrawer.value = false
  }
}

const navigateToProfile = () => {
  router.push('/settings/profile')
}

const handleCheckForUpdates = () => {
  if (!electronAutoUpdate) return
  void electronAutoUpdate.checkForUpdates({ manual: true })
}

const handleLogout = async () => {
  await authStore.logout()
  await router.replace('/login')
}
</script>

<style lang="scss" scoped>
.user-menu {
  .user-menu-btn {
    border-radius: 50%;
    padding: 4px;
    color: rgba(0, 0, 0, 0.8);
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.5);
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .q-avatar {
      font-size: 0.875rem;
      font-weight: 600;
    }
  }

  .user-menu-dropdown {
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(10px);
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.2);

    .user-menu-item {
      border-radius: 8px;
      margin: 2px 8px;
      transition: all 0.2s ease;

      &:hover {
        background: rgba(128, 191, 239, 0.1);
      }
    }
  }
}
</style>
